import { useEffect, useState } from "preact/hooks";
import { AdminView } from "./admin";
import { ApiError, api, type Me } from "./api";
import { Dashboard } from "./dashboard";
import { MessageView } from "./message";

export function navigate(to: string): void {
  history.pushState(null, "", to);
  dispatchEvent(new PopStateEvent("popstate"));
}

/** Client-side link: same-tab navigation without a page load. */
export function Link(props: { href: string; class?: string; children: preact.ComponentChildren }) {
  return (
    <a
      href={props.href}
      class={props.class}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(props.href);
      }}
    >
      {props.children}
    </a>
  );
}

function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);
  return path;
}

function SignIn() {
  const returnTo = encodeURIComponent(location.pathname + location.search);
  return (
    <div class="center">
      <div class="card stack">
        <img src="/favicon.svg" alt="" width="48" height="48" />
        <h2>Sembit Email Search</h2>
        <p class="muted">
          Search your own Sembit mailbox from ChatGPT and Claude. Nothing here sends mail, deletes anything permanently or marks mail read; tidying up moves mail to Trash or Junk only after you approve a preview.
        </p>
        <a class="button primary" href={`/login?return=${returnTo}`}>
          Sign in with GitLab
        </a>
      </div>
    </div>
  );
}

function TopBar({ me }: { me: Me }) {
  return (
    <header class="topbar">
      <Link href="/" class="brand">
        <img src="/favicon.svg" alt="" />
        Sembit Email Search
      </Link>
      <div class="who">
        {me.user.isAdmin && <Link href="/admin">Admin</Link>}
        <span>{me.user.email}</span>
        <form method="post" action="/logout">
          <button type="submit">Sign out</button>
        </form>
      </div>
    </header>
  );
}

export function App() {
  const path = usePath();
  const [me, setMe] = useState<Me | null>(null);
  const [state, setState] = useState<"loading" | "signed-out" | "ready" | "error">("loading");
  const [error, setError] = useState("");

  const loadMe = () =>
    api
      .me()
      .then((m) => {
        setMe(m);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) setState("signed-out");
        else {
          setError(String((err as Error).message));
          setState("error");
        }
      });

  useEffect(() => {
    void loadMe();
  }, []);

  if (state === "loading") return <div class="center muted">Loading…</div>;
  if (state === "signed-out") return <SignIn />;
  if (state === "error" || !me) return <div class="center"><p class="error-text">{error || "Something went wrong."}</p></div>;

  const messageMatch = /^\/m\/(\d+)$/.exec(path);
  return (
    <div class="shell">
      <TopBar me={me} />
      {messageMatch ? (
        <MessageView id={messageMatch[1]!} />
      ) : path === "/admin" && me.user.isAdmin ? (
        <AdminView selfEmail={me.user.email} />
      ) : (
        <Dashboard me={me} onCredentialsChanged={loadMe} />
      )}
    </div>
  );
}
